/**
 * Sprint 207P — Peer Invite Landing
 *
 * Captures `/join/:code` (the link shared by a teen via PeerInviteCard),
 * stashes the code in localStorage as `hibff_peer_invite_code`, and
 * routes to /register. After the new user signs up + lands on the
 * dashboard, `useAcceptPeerInvite` (mounted in App.js) reads that key
 * and posts `/api/peer-invites/accept` to bind the peer relation.
 */
import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Users } from 'lucide-react';

const PeerInviteLanding = () => {
  const { code } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (code) {
      try {
        localStorage.setItem('hibff_peer_invite_code', code.toUpperCase());
      } catch {}
    }
    // Tiny delay so the welcome banner is visible before redirect.
    const t = setTimeout(() => navigate('/register', { replace: true }), 1200);
    return () => clearTimeout(t);
  }, [code, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center" data-testid="peer-invite-landing">
        <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
          <Users size={28} className="text-white" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">You&apos;ve been invited 🎉</h1>
        <p className="text-gray-600 text-sm mb-6">
          A friend wants you to join them on HiBFF — when you finish your first challenge,
          they&apos;ll earn a Support Trophy and you&apos;ll start your journey.
        </p>
        <p className="text-xs text-gray-400 inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Setting things up…
        </p>
        {code && (
          <p className="mt-4 text-[11px] text-gray-400 font-mono" data-testid="peer-invite-code">
            Invite code: {code.toUpperCase()}
          </p>
        )}
      </div>
    </div>
  );
};

export default PeerInviteLanding;
